/**
 * Centralized mobile-specific style overrides
 * 
 * Global CSS rules that apply mobile-first design principles
 * and ensure consistent touch-friendly interactions across the app.
 * 
 * @module mobileOverrides
 */
import { createGlobalStyle } from 'styled-components';

export const MobileOverrides = createGlobalStyle`
  /* ============================================ */
  /* MOBILE OPTIMIZATIONS (max-width: 768px)     */
  /* ============================================ */
  
  @media (max-width: 768px) {
    /* ------------------------------------------ */
    /* Touch Targets - WCAG 2.1 Compliance       */
    /* ------------------------------------------ */
    
    /* Ensure all interactive elements meet minimum touch target size */
    button, 
    a, 
    [role="button"],
    [onClick],
    .clickable {
      min-height: 44px;
      min-width: 44px;
      touch-action: manipulation; /* Prevent double-tap zoom */
    }
    
    /* ------------------------------------------ */
    /* Scroll Indicators                          */
    /* ------------------------------------------ */
    
    /* Visual indicator when content can be scrolled horizontally */
    .chart-scroll-container {
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 20px;
        height: 100%;
        background: linear-gradient(to right, rgba(255,255,255,1), transparent);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 100;
      }
      
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        width: 30px;
        height: 100%;
        background: linear-gradient(to left, rgba(255,255,255,1), transparent);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 100;
      }
      
      &.has-left-scroll::before {
        opacity: 1;
      }
      
      &.has-right-scroll::after {
        opacity: 1;
      }
    }
    
    /* ------------------------------------------ */
    /* Loading States                             */
    /* ------------------------------------------ */
    
    /* More transparent loading overlay to see previous chart */
    .overlay-loader {
      background: rgba(255,255,255,0.92) !important;
      backdrop-filter: blur(2px);
    }
    
    /* ------------------------------------------ */
    /* Form Elements                              */
    /* ------------------------------------------ */
    
    /* Prevent iOS zoom when focusing inputs/selects */
    select, 
    input, 
    textarea {
      font-size: 16px !important;
    }
    
    /* Better select styling on mobile */
    select {
      height: 44px !important;
      padding: 10px 12px !important;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
      padding-right: 32px !important;
    }
    
    /* ------------------------------------------ */
    /* Smooth Scrolling                           */
    /* ------------------------------------------ */
    
    /* Enable momentum scrolling on iOS */
    .chart-wrapper,
    .scroll-container,
    [data-scroll="true"] {
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
    }
    
    /* ------------------------------------------ */
    /* Typography Adjustments                     */
    /* ------------------------------------------ */
    
    /* Ensure readable text sizes */
    body {
      -webkit-text-size-adjust: 100%;
      -moz-text-size-adjust: 100%;
      text-size-adjust: 100%;
    }
    
    /* Improve text rendering on mobile */
    * {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    
    /* ------------------------------------------ */
    /* Active/Hover States for Touch             */
    /* ------------------------------------------ */
    
    /* Provide visual feedback on touch */
    button:active,
    [role="button"]:active,
    .clickable:active {
      opacity: 0.7;
      transform: scale(0.98);
      transition: all 0.1s ease;
    }
    
    /* Remove hover effects on touch devices */
    @media (hover: none) {
      *:hover {
        /* Prevent hover states on touch devices */
      }
    }
    
    /* ------------------------------------------ */
    /* Chart Specific Optimizations              */
    /* ------------------------------------------ */
    
    /* Reduce padding on chart containers */
    .chart-container {
      padding: 1rem !important;
    }
    
    /* Better spacing for legends */
    .legenda-candidatos {
      gap: 0.8rem !important;
      padding: 1rem 0.5rem !important;
    }
    
    /* ------------------------------------------ */
    /* Performance Optimizations                  */
    /* ------------------------------------------ */
    
    /* Use GPU acceleration for animations */
    .animated,
    [data-animate="true"] {
      will-change: transform;
      transform: translateZ(0);
    }
  }
  
  /* ============================================ */
  /* TABLET OPTIMIZATIONS (768px - 1024px)      */
  /* ============================================ */
  
  @media (min-width: 769px) and (max-width: 1024px) {
    /* Slightly larger touch targets for tablets */
    button, 
    a, 
    [role="button"] {
      min-height: 40px;
      min-width: 40px;
    }
    
    /* Adjust font sizes for tablet */
    select, 
    input, 
    textarea {
      font-size: 15px !important;
    }
  }
  
  /* ============================================ */
  /* LANDSCAPE OPTIMIZATIONS                    */
  /* ============================================ */
  
  @media (max-width: 1024px) and (orientation: landscape) {
    /* Reduce vertical spacing in landscape mode */
    .container,
    .chart-container {
      padding-top: 0.75rem !important;
      padding-bottom: 0.75rem !important;
    }
    
    /* Optimize vertical space usage */
    h1, h2, h3 {
      margin-top: 0.5rem !important;
      margin-bottom: 0.5rem !important;
    }
  }
  
  /* ============================================ */
  /* PRINT OPTIMIZATIONS                        */
  /* ============================================ */
  
  @media print {
    /* Hide interactive elements when printing */
    button,
    .overlay-loader,
    .scroll-indicator {
      display: none !important;
    }
    
    /* Show all chart content */
    .chart-wrapper {
      overflow: visible !important;
      max-width: 100% !important;
    }
  }
  
  /* ============================================ */
  /* ACCESSIBILITY ENHANCEMENTS                 */
  /* ============================================ */
  
  /* Focus indicators for keyboard navigation */
  @media (max-width: 768px) {
    *:focus-visible {
      outline: 3px solid #1e88e5 !important;
      outline-offset: 2px !important;
    }
    
    /* Skip to content link for accessibility */
    .skip-to-content {
      position: absolute;
      top: -40px;
      left: 0;
      background: #1e88e5;
      color: white;
      padding: 8px 16px;
      text-decoration: none;
      z-index: 10000;
      
      &:focus {
        top: 0;
      }
    }
  }
  
  /* ============================================ */
  /* DARK MODE SUPPORT (if enabled)             */
  /* ============================================ */
  
  @media (prefers-color-scheme: dark) {
    /* Dark mode optimizations would go here */
    /* Currently not implemented, but structure ready */
  }
`;

